import { RefObject, useMemo } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import {
  ChangePasswordPayload,
  ChangeSecretPayload,
  UpdatePasswordError,
  useChangePassword,
  useChangeSecret,
} from "@companion-core/shared/app/Queries";
import { MyAccountSubModule } from "@companion-core/shared/app/Interfaces/myAccount";
import { getPasswordError } from "@companion-core/shared/app/Utils/errors";
import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { DynamicFormJson, Field } from "@companion-core/shared/app/Interfaces/wizard";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { AxiosError } from "axios";

export const useSecurity = (scrollRef?: RefObject<HTMLDivElement | null>) => {
  const { t } = useTranslation();
  const myAccountModule = getModule("my-account");
  const { setValidationStatus, setValidationMessage, resetValidationBanner } = useMemberStore();
  const queryClient = useQueryClient();
  const isMemberFetching = useIsFetching({ queryKey: ["member"] });
  const dataMember = queryClient.getQueryData(["member"]) as APIMemberData | null;
  const basesJson = {
    baseJsonChangeSecret: loadFormFromPath(
      myAccountModule?.modules?.security?.forms?.secretFormPath ??
        "common-forms/myAccount/security/changeSecret"
    ),
    baseJsonChangePassword: loadFormFromPath(
      myAccountModule?.modules?.security?.forms?.passwordFormPath ??
        "common-forms/myAccount/security/changePassword"
    ),
  };
  const { mutateAsync: changeSecret, isPending: isPendingChangeSecret } = useChangeSecret(() => {
    resetStepsFields();
    setValidationStatus("error");
    setValidationMessage(t("common.validation-banner.error"));
  });
  const { mutateAsync: changePassword, isPending: isPendingChangePassword } = useChangePassword(
    (error: AxiosError<UpdatePasswordError>) => {
      resetStepsFields();
      setValidationStatus("error");
      setValidationMessage(t(`my-account.security.password.error.${getPasswordError(error)}`));
    }
  );
  const securityConfig = useMemo(() => myAccountModule?.modules?.security, [myAccountModule]);
  const enabledModulesCount = useMemo(
    () =>
      Object.entries(myAccountModule?.modules)?.filter(
        (module) => !!(module[1] as MyAccountSubModule)?.enabled
      ).length,
    [myAccountModule]
  );

  const resetStepsFields = () => {
    Object.values(basesJson).forEach((dynamicForm: DynamicFormJson) => {
      dynamicForm?.steps[0]?.fields.forEach((field: Field) => {
        field.value = "";
      });
    });
  };

  const onSubmitChangeSecretForm = async (payload: Record<string, unknown>) => {
    // TO DO: click submit tracking
    resetValidationBanner();
    const formattedPayload = {
      ...payload,
      username: dataMember?.username ?? "",
    } as ChangeSecretPayload;
    try {
      await changeSecret({ payload: formattedPayload });
      resetStepsFields();
      setValidationStatus("success");
      setValidationMessage(t("my-account.security.success"));
      scrollRef?.current?.scrollIntoView({ behavior: "smooth" });
    } catch {
      scrollRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onSubmitChangePasswordForm = async (
    payload: Record<string, unknown>
  ): Promise<ChangePasswordPayload | undefined> => {
    // TO DO: click submit tracking
    resetValidationBanner();
    const formattedPayload = {
      ...payload,
      username: dataMember?.username ?? "",
    } as ChangePasswordPayload;
    try {
      const hashedPayload = await changePassword({ payload: formattedPayload });
      resetStepsFields();
      setValidationStatus("success");
      setValidationMessage(t("my-account.security.success"));
      scrollRef?.current?.scrollIntoView({ behavior: "smooth" });
      return hashedPayload as ChangePasswordPayload;
    } catch {
      scrollRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return {
    isMemberFetching,
    dataMember,
    securityConfig,
    jsonForms: basesJson,
    enabledModulesCount,
    isPendingChangeSecret,
    isPendingChangePassword,
    t,
    onSubmitChangeSecretForm,
    onSubmitChangePasswordForm,
  };
};
